// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import type {UserProfile} from '@mattermost/types/users';

import {isGuest} from 'mattermost-redux/utils/user_utils';

import AlertIcon from 'components/widgets/icons/alert_icon';
import EmailIcon from 'components/widgets/icons/mail_icon';
import BotTag from 'components/widgets/tag/bot_tag';
import GuestTag from 'components/widgets/tag/guest_tag';
import Avatar from 'components/widgets/users/avatar';

import {imageURLForUser, getLongDisplayName} from 'utils/utils';

import './result_table.scss';

type InviteNotSent = {
    text: React.ReactNode | React.ReactNodeArray;
}

type InviteEmail = {
    email: string;
}

type InviteUser = {
    user: UserProfile;
}

type I18nLike = {
    id: string;
    defaultMessage: string;
    values?: Record<string, React.ReactNode>;
}

export type InviteResult = (InviteNotSent | InviteEmail | InviteUser) & {
    reason: string | I18nLike;
    path?: string;
}

export type Props = {
    sent?: boolean;
    rows: InviteResult[];
}

export default function ResultTable(props: Props) {
    const intl = useIntl();
    let wrapperClass = 'invitation-modal-confirm invitation-modal-confirm--not-sent';
    let header = (
        <h2>
            <FormattedMessage
                id='invitation_modal.confirm.not-sent-header'
                defaultMessage='Invitations Not Sent'
            />
        </h2>
    );
    if (props.sent) {
        wrapperClass = 'invitation-modal-confirm invitation-modal-confirm--sent';
        header = (
            <h2>
                <FormattedMessage
                    id='invitation_modal.confirm.sent-header'
                    defaultMessage='Successful Invites'
                />
            </h2>
        );
    }

    function messageWithLink(reason: I18nLike, link: string) {
        return intl.formatMessage(
            reason,
            {
                a: (chunks: React.ReactNode | React.ReactNodeArray) => (
                    <a
                        href={link}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            window.open(link);
                        }}
                    >
                        {chunks}
                    </a>
                ),
            },
        );
    }
    return (
        <div className={wrapperClass}>
            {header}
            <table className='InviteResultTable'>
                <thead>
                    <tr className='table-header'>
                        <th className='people-header'>
                            <FormattedMessage
                                id='invitation-modal.confirm.people-header'
                                defaultMessage='People'
                            />
                        </th>
                        <th className='details-header'>
                            <FormattedMessage
                                id='invitation-modal.confirm.details-header'
                                defaultMessage='Details'
                            />
                        </th>
                    </tr>
                </thead>
                <tbody className='rows'>
                    {props.rows.map((invitation: InviteResult) => {
                        let icon;
                        let username;
                        let className;
                        let guestBadge;
                        let botBadge;
                        let reactKey = '';

                        if (Object.hasOwn(invitation, 'user')) {
                            className = 'name';
                            const user = (invitation as InviteUser).user;
                            reactKey = user.id;
                            const profileImg = imageURLForUser(user.id, user.last_picture_update);
                            icon = (
                                <Avatar
                                    username={user.username}
                                    url={profileImg}
                                    size='lg'
                                />
                            );
                            username = getLongDisplayName(user);
                            if (user.is_bot) {
                                botBadge = <BotTag/>;
                            }
                            if (isGuest(user.roles)) {
                                guestBadge = <GuestTag/>;
                            }
                        } else if (Object.hasOwn(invitation, 'email')) {
                            const email = (invitation as InviteEmail).email;
                            reactKey = email;
                            className = 'email';
                            icon = <EmailIcon className='mail-icon'/>;
                            username = email;
                        } else {
                            const text = (invitation as InviteNotSent).text;
                            reactKey = typeof text === 'string' ? text : text?.toString() || 'result_table_unknown_text';
                            className = 'name';
                            icon = <AlertIcon className='alert-icon'/>;
                            username = text;
                        }

                        let reason;
                        if (typeof invitation.reason === 'string') {
                            reason = invitation.reason;
                        } else if (invitation.path) {
                            reason = messageWithLink(invitation.reason, invitation.path);
                        } else {
                            reason = (
                                <FormattedMessage
                                    id={invitation.reason.id}
                                    defaultMessage={invitation.reason.defaultMessage}
                                    values={invitation.reason.values}
                                />
                            );
                        }

                        return (
                            <tr
                                key={reactKey}
                                className='InviteResultRow'
                            >
                                <td className='username-or-icon'>
                                    {icon}
                                    <span className={className}>
                                        {username}
                                        {botBadge}
                                        {guestBadge}
                                    </span>
                                </td>
                                <td className='reason'>
                                    {reason}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
